import React, { useState, useMemo, useEffect } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    Grid,
    IconButton,
    MenuItem,
    Skeleton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import HistoryIcon from '@mui/icons-material/History'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined'
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined'
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined'
import GetAppIcon from '@mui/icons-material/GetApp'
import { useForm } from 'react-hook-form'
import { get } from '../../common/constants'
import { FormProvider, RHFSelectField, RHFSearchableSelectField } from '../../common/hook-form'
import { useGetAllMerchantsIdAndNameQuery, useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls'
import { DownloadExcelMultipleSheets } from '../../helpers/ExcelGenerator'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import Loader from '../../common/Loader'
import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'
import { useSnackbar } from 'notistack'

// ─── Theme tokens ───────────────────────────────────────────────────────────────
const COLORS = {
    primary: '#1565c0',
    primaryLight: '#D1E9FC',
    primaryDark: '#103996',
    success: '#229A16',
    successLight: '#E8F5E9',
    info: '#1890FF',
    infoLight: '#E3F2FD',
    warning: '#B78103',
    warningLight: '#FFF8E1',
    grey50: '#F9FAFB',
    grey100: '#F4F6F8',
    grey200: '#DFE3E8',
    grey300: '#C4CDD5',
    grey600: '#637381',
    grey800: '#212B36',
    headerGradient: 'linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)',
}

// ─── Column definitions ─────────────────────────────────────────────────────────

const b2cColumns = [
    { field: 'zoneName', headerName: 'Zone', width: 150, headerAlign: 'center', align: 'center' },
    { field: 'startWt', headerName: 'Start Wt (gm)', width: 120, headerAlign: 'center', align: 'center' },
    { field: 'minWt', headerName: 'Min Wt (gm)', width: 120, headerAlign: 'center', align: 'center' },
    { field: 'maxWt', headerName: 'Max Wt (gm)', width: 120, headerAlign: 'center', align: 'center' },
    { field: 'price', headerName: 'Price', width: 100, headerAlign: 'center', align: 'center', renderCell: (p) => p.row?.price ? '₹ ' + p.row.price : '-' },
    { field: 'additionalUnit', headerName: 'Addl Unit (gm)', width: 120, headerAlign: 'center', align: 'center', renderCell: (p) => p.row?.additionalUnit ?? '-' },
    { field: 'additionalUnitRate', headerName: 'Addl Rate', width: 100, headerAlign: 'center', align: 'center', renderCell: (p) => p.row?.additionalUnitRate ? '₹ ' + p.row.additionalUnitRate : '-' },
    { field: 'volumetricCoeff', headerName: 'Vol. Coeff', width: 100, headerAlign: 'center', align: 'center', renderCell: (p) => p.row?.volumetricCoeff ?? '-' },
    { field: 'taxPercent', headerName: 'Tax %', width: 80, headerAlign: 'center', align: 'center', renderCell: (p) => p.row?.taxPercent ? p.row.taxPercent + '%' : '-' },
    { field: 'surchargePercent', headerName: 'FSC %', width: 80, headerAlign: 'center', align: 'center', renderCell: (p) => p.row?.surchargePercent ? p.row.surchargePercent + '%' : '-' },
    { field: 'docketCharge', headerName: 'Docket', width: 90, headerAlign: 'center', align: 'center', renderCell: (p) => p.row?.docketCharge ? '₹ ' + p.row.docketCharge : '-' },
    { field: 'codOperator', headerName: 'COD Op', width: 90, headerAlign: 'center', align: 'center', renderCell: (p) => p.row?.codOperator ?? '-' },
    { field: 'fixedCODCharges', headerName: 'COD Charges', width: 110, headerAlign: 'center', align: 'center', renderCell: (p) => p.row?.fixedCODCharges ? '₹ ' + p.row.fixedCODCharges : '-' },
    { field: 'withQCCharge', headerName: 'With QC', width: 100, headerAlign: 'center', align: 'center', renderCell: (p) => p.row?.withQCCharge ? '₹ ' + p.row.withQCCharge : '-' },
    { field: 'withoutQCCharge', headerName: 'W/o QC', width: 100, headerAlign: 'center', align: 'center', renderCell: (p) => p.row?.withoutQCCharge ? '₹ ' + p.row.withoutQCCharge : '-' },
]

const b2bColumns = [
    { field: 'zoneKey', headerName: 'Zone', width: 140, sortable: false, renderCell: (p) => p.row.zoneKey || p.row.zoneName || '-' },
    { field: 'startWt', headerName: 'Start Wt (gm)', width: 120, sortable: false, renderCell: (p) => p.row.slab?.[0]?.startWt ?? '-' },
    { field: 'maxWt', headerName: 'Max Wt (gm)', width: 120, sortable: false, renderCell: (p) => p.row.slab?.[0]?.maxWt ?? '-' },
    { field: 'price', headerName: 'Price', width: 100, sortable: false, renderCell: (p) => p.row.slab?.[0]?.price ? '₹ ' + p.row.slab[0].price : '-' },
    { field: 'additionalUnit', headerName: 'Addl Unit', width: 100, sortable: false, renderCell: (p) => p.row.slab?.[0]?.additionalUnit ?? '-' },
    { field: 'additionalUnitRate', headerName: 'Addl Rate', width: 100, sortable: false, renderCell: (p) => p.row.slab?.[0]?.additionalUnitRate ?? '-' },
    { field: 'volumetricCoeff', headerName: 'Vol. Coeff', width: 100, sortable: false, renderCell: (p) => p.row.slab?.[0]?.volumetricCoeff ?? '-' },
    { field: 'taxPercent', headerName: 'Tax %', width: 80, sortable: false, renderCell: (p) => p.row.slab?.[0]?.taxPercent ? p.row.slab[0].taxPercent + '%' : '-' },
    { field: 'surchargePercent', headerName: 'Surcharge %', width: 100, sortable: false, renderCell: (p) => p.row.slab?.[0]?.surchargePercent ? p.row.slab[0].surchargePercent + '%' : '-' },
    { field: 'docketCharge', headerName: 'Docket', width: 90, sortable: false, renderCell: (p) => p.row.slab?.[0]?.docketCharge ?? '-' },
    { field: 'codOperator', headerName: 'COD Op', width: 90, sortable: false, renderCell: (p) => p.row.slab?.[0]?.codOperator ?? '-' },
    { field: 'minChargableAmount', headerName: 'Min Charge Amt', width: 130, sortable: false, renderCell: (p) => p.row.slab?.[0]?.minChargableAmount ?? '-' },
    { field: 'minChargableWeight', headerName: 'Min Charge Wt', width: 130, sortable: false, renderCell: (p) => p.row.slab?.[0]?.minChargableWeight ?? '-' },
]

// Excel export column mappings
const b2cExcelCols = [
    { label: 'Zone', value: 'zoneName' },
    { label: 'Start Wt (gm)', value: 'startWt' },
    { label: 'Min Wt (gm)', value: 'minWt' },
    { label: 'Max Wt (gm)', value: 'maxWt' },
    { label: 'Price', value: 'price' },
    { label: 'Addl Unit (gm)', value: 'additionalUnit' },
    { label: 'Addl Rate', value: 'additionalUnitRate' },
    { label: 'Vol. Coeff', value: 'volumetricCoeff' },
    { label: 'Tax %', value: 'taxPercent' },
    { label: 'FSC %', value: 'surchargePercent' },
    { label: 'Docket Charge', value: 'docketCharge' },
    { label: 'COD Operator', value: 'codOperator' },
    { label: 'COD Charges', value: 'fixedCODCharges' },
    { label: 'With QC', value: 'withQCCharge' },
    { label: 'Without QC', value: 'withoutQCCharge' },
]

const b2bExcelCols = [
    { label: 'Zone', value: 'zoneKey' },
    { label: 'Start Wt (gm)', value: (row) => row.slab?.[0]?.startWt ?? '' },
    { label: 'Max Wt (gm)', value: (row) => row.slab?.[0]?.maxWt ?? '' },
    { label: 'Price', value: (row) => row.slab?.[0]?.price ?? '' },
    { label: 'Addl Unit', value: (row) => row.slab?.[0]?.additionalUnit ?? '' },
    { label: 'Addl Rate', value: (row) => row.slab?.[0]?.additionalUnitRate ?? '' },
    { label: 'Vol. Coeff', value: (row) => row.slab?.[0]?.volumetricCoeff ?? '' },
    { label: 'Tax %', value: (row) => row.slab?.[0]?.taxPercent ?? '' },
    { label: 'Surcharge %', value: (row) => row.slab?.[0]?.surchargePercent ?? '' },
    { label: 'Docket Charge', value: (row) => row.slab?.[0]?.docketCharge ?? '' },
    { label: 'COD Operator', value: (row) => row.slab?.[0]?.codOperator ?? '' },
    { label: 'Min Charge Amt', value: (row) => row.slab?.[0]?.minChargableAmount ?? '' },
    { label: 'Min Charge Wt', value: (row) => row.slab?.[0]?.minChargableWeight ?? '' },
]

const SEARCH_TYPE_OPTIONS = [
    { label: 'Merchant', value: 'merchant' },
    { label: 'Courier', value: 'courier' },
]
const SHIP_TYPE_OPTIONS = [
    { label: 'B2C', value: 'B2C' },
    { label: 'B2B', value: 'B2B' },
]

// ─── Helpers ────────────────────────────────────────────────────────────────────

function flattenSlaToRows(slaObj, isB2B, entryIdx) {
    if (!slaObj || typeof slaObj !== 'object') return []
    if (isB2B) {
        return Object.entries(slaObj)
            .filter(([, val]) => val?.slab && val.slab.length > 0)
            .map(([key, val]) => ({ ...val, zoneKey: val.zoneName || val.zone || key, _id: `${entryIdx}_${key}` }))
    }
    const rows = []
    Object.entries(slaObj).forEach(([key, val]) => {
        if (val?.slab?.length > 0) {
            val.slab.forEach((slab, idx) => {
                rows.push({ ...slab, zoneName: val.zoneName || key, _id: `${entryIdx}_${key}_${idx}` })
            })
        }
    })
    return rows
}

function formatSnapshotTime(snapshot) {
    const raw = snapshot.snapshotTime || (snapshot.snapshotTimestamp ? new Date(snapshot.snapshotTimestamp).toISOString() : null)
    if (!raw) return 'Unknown'
    return new Date(raw).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function formatShortDate(snapshot) {
    const raw = snapshot?.snapshotTime || (snapshot?.snapshotTimestamp ? new Date(snapshot.snapshotTimestamp).toISOString() : null)
    if (!raw) return ''
    return new Date(raw).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

// ─── KPI stat box (responsive) ──────────────────────────────────────────────────

function KpiBox({ icon, label, value, color, bg }) {
    return (
        <Tooltip title={String(value)} placement="top" arrow>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1.5,
                borderRadius: 2,
                border: `1px solid ${color}20`,
                backgroundColor: bg,
                minWidth: 0,
                flex: { xs: '1 1 calc(50% - 8px)', md: '1 1 0' },
            }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: 1.5,
                    backgroundColor: '#fff',
                    border: `1px solid ${color}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: COLORS.grey600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                        {label}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.grey800, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {value}
                    </Typography>
                </Box>
            </Box>
        </Tooltip>
    )
}

// ─── Snapshot prev/next nav ─────────────────────────────────────────────────────

function SnapshotTimeline({ snapshots, selectedIdx, onSelect }) {
    if (snapshots.length <= 1) return null
    const canPrev = selectedIdx < snapshots.length - 1
    const canNext = selectedIdx > 0
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Newer">
                <span>
                    <IconButton size="small" disabled={!canNext} onClick={() => onSelect(selectedIdx - 1)}>
                        <NavigateBeforeIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Typography variant="caption" sx={{ color: COLORS.grey600, fontWeight: 600, minWidth: 60, textAlign: 'center' }}>
                {selectedIdx + 1} / {snapshots.length}
            </Typography>
            <Tooltip title="Older">
                <span>
                    <IconButton size="small" disabled={!canPrev} onClick={() => onSelect(selectedIdx + 1)}>
                        <NavigateNextIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    )
}

// ─── Grid skeleton (loading transition) ─────────────────────────────────────────

function GridSkeleton() {
    return (
        <Box sx={{ px: 2, py: 2 }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                {[140, 100, 100, 80, 80, 100, 80, 60, 60, 80].map((w, i) => (
                    <Skeleton key={i} variant="rounded" width={w} height={32} sx={{ borderRadius: 1 }} />
                ))}
            </Box>
            {/* Data rows */}
            {Array.from({ length: 8 }).map((_, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    {[140, 100, 100, 80, 80, 100, 80, 60, 60, 80].map((w, j) => (
                        <Skeleton key={j} variant="text" width={w} height={24} />
                    ))}
                </Box>
            ))}
        </Box>
    )
}

// ─── Landing / Welcome State ────────────────────────────────────────────────────

const FEATURES = [
    { icon: <AccessTimeIcon />, title: 'Full Audit Trail', desc: 'Every rate card change is captured with timestamps and user details for complete traceability.', color: COLORS.primary, bg: COLORS.primaryLight + '60' },
    { icon: <CompareArrowsOutlinedIcon />, title: 'B2B & B2C Support', desc: 'View history for both B2C and B2B ship types across merchants and courier partners.', color: '#7b1fa2', bg: '#F3E5F560' },
    { icon: <LocalShippingOutlinedIcon />, title: 'Per-Courier Configs', desc: 'Browse individual courier-specific rate cards within each snapshot, including default configs.', color: COLORS.warning, bg: COLORS.warningLight },
    { icon: <SecurityOutlinedIcon />, title: 'Zone-Level Detail', desc: 'Full slab-level pricing data for every zone — weights, taxes, surcharges, COD charges and more.', color: COLORS.success, bg: COLORS.successLight },
]

const STEPS = [
    { step: '1', label: 'Select merchant or courier partner' },
    { step: '2', label: 'Choose B2B or B2C ship type' },
    { step: '3', label: 'Browse snapshots & view rate details' },
]

function LandingState() {
    return (
        <Box sx={{ mt: 1 }}>
            <Card sx={{ overflow: 'hidden', mb: 2.5 }}>
                <Box sx={{
                    background: `linear-gradient(135deg, ${COLORS.primaryLight}80 0%, #E3F2FD 50%, #F3E5F530 100%)`,
                    px: { xs: 2.5, md: 4 }, py: { xs: 4, md: 5 },
                    textAlign: 'center',
                    position: 'relative',
                }}>
                    <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', backgroundColor: alpha(COLORS.primary, 0.04) }} />
                    <Box sx={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', backgroundColor: alpha('#7b1fa2', 0.04) }} />
                    <Box sx={{
                        width: 64, height: 64, borderRadius: 3,
                        background: COLORS.headerGradient,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 2.5,
                        boxShadow: `0 8px 24px ${alpha(COLORS.primary, 0.25)}`,
                    }}>
                        <HistoryIcon sx={{ color: '#fff', fontSize: 32 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.35rem' }, color: COLORS.grey800, mb: 1 }}>
                        Rate Card History Viewer
                    </Typography>
                    <Typography sx={{ color: COLORS.grey600, fontSize: '0.88rem', maxWidth: 520, mx: 'auto', lineHeight: 1.6 }}>
                        Track every pricing change across your merchant and courier rate cards.
                        Search by entity, browse timestamped snapshots, and drill into zone-level slab details.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 2, md: 3 }, mt: 3.5, flexWrap: 'wrap' }}>
                        {STEPS.map((s, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    background: COLORS.headerGradient,
                                    color: '#fff', fontWeight: 700, fontSize: '0.8rem',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    {s.step}
                                </Box>
                                <Typography sx={{ color: COLORS.grey800, fontSize: '0.82rem', fontWeight: 500, textAlign: 'left' }}>
                                    {s.label}
                                </Typography>
                                {i < STEPS.length - 1 && (
                                    <Box sx={{ width: 32, height: 1, backgroundColor: COLORS.grey300, ml: 1, display: { xs: 'none', md: 'block' } }} />
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Card>
            <Grid container spacing={2}>
                {FEATURES.map((f, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                        <Card sx={{
                            height: '100%', borderRadius: 3, border: `1px solid ${f.color}15`,
                            transition: 'all 0.2s ease',
                            '&:hover': { borderColor: `${f.color}40`, boxShadow: `0 4px 20px ${alpha(f.color, 0.08)}`, transform: 'translateY(-2px)' },
                        }}>
                            <Box sx={{ p: 2.5 }}>
                                <Box sx={{
                                    width: 44, height: 44, borderRadius: 2,
                                    backgroundColor: f.bg, border: `1px solid ${f.color}25`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
                                }}>
                                    {React.cloneElement(f.icon, { sx: { fontSize: 22, color: f.color } })}
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: COLORS.grey800, mb: 0.5 }}>{f.title}</Typography>
                                <Typography sx={{ color: COLORS.grey600, fontSize: '0.78rem', lineHeight: 1.55 }}>{f.desc}</Typography>
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    )
}

// ─── Main Component ─────────────────────────────────────────────────────────────

function RateCardHistory() {
    const { enqueueSnackbar } = useSnackbar()
    const [loading, setLoading] = useState(false)
    const [snapshots, setSnapshots] = useState([])
    const [searched, setSearched] = useState(false)
    const [selectedSnapshotIdx, setSelectedSnapshotIdx] = useState(0)
    const [selectedConfigIdx, setSelectedConfigIdx] = useState(0)
    const [gridLoading, setGridLoading] = useState(false)
    const [searchContext, setSearchContext] = useState(null) // { name, type, shipType }

    const methods = useForm({ defaultValues: { searchType: 'merchant', refId: '', shipType: 'B2C' } })
    const { handleSubmit, reset, watch, setValue } = methods
    const searchType = watch('searchType')
    const shipType = watch('shipType')

    // Reset refId when switching between merchant/courier
    useEffect(() => { setValue('refId', '') }, [searchType, setValue])

    // Brief skeleton transition when switching snapshots/configs
    useEffect(() => {
        if (snapshots.length === 0) return
        setGridLoading(true)
        const t = setTimeout(() => setGridLoading(false), 150)
        return () => clearTimeout(t)
    }, [selectedSnapshotIdx, selectedConfigIdx])

    const { data: allMerchants = [] } = useGetAllMerchantsIdAndNameQuery()
    const courierFilter = useMemo(() => ({
        andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
        offset: 0, limit: 50000, skipCount: true,
        includes: 'id,name,cpAccountCode,prozoId,parent',
    }), [])
    const { data: allCouriers = [] } = useGetAllCourierPartnersCachedQuery(courierFilter)

    const merchantOptions = useMemo(() => {
        if (!Array.isArray(allMerchants)) return []
        return allMerchants
            .filter((m) => m.is_deleted !== '1' && m.is_deleted !== true && m.name)
            .map((m) => ({ label: m.name, value: m.id }))
            .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
    }, [allMerchants])

    const courierOptions = useMemo(() => {
        if (!Array.isArray(allCouriers)) return []
        return allCouriers
            .filter((c) => c.cpAccountCode && c.name)
            .map((c) => ({ label: `${c.name} (${c.cpAccountCode})`, value: c.cpAccountCode }))
            .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
    }, [allCouriers])

    const selectedSnapshot = snapshots[selectedSnapshotIdx] || null

    const configEntries = useMemo(() => {
        if (!selectedSnapshot?.rateCardData?.value) return []
        return selectedSnapshot.rateCardData.value.map((entry, idx) => {
            const label = entry.isDefault ? 'Base Rate Card (All Couriers)' : entry.cplist?.length ? entry.cplist.join(', ') : `Config #${idx + 1}`
            return { label, entry, idx }
        })
    }, [selectedSnapshot])

    const selectedConfig = configEntries[selectedConfigIdx]?.entry || null

    const gridRows = useMemo(() => {
        if (!selectedConfig?.sla) return []
        return flattenSlaToRows(selectedConfig.sla, shipType === 'B2B', selectedConfigIdx)
    }, [selectedConfig, shipType, selectedConfigIdx])

    const currentColumns = shipType === 'B2B' ? b2bColumns : b2cColumns

    const zoneCount = useMemo(() => {
        if (!selectedConfig?.sla) return 0
        return Object.values(selectedConfig.sla).filter(z => z?.slab?.length > 0).length
    }, [selectedConfig])

    // Excel export data
    const excelSheets = useMemo(() => {
        if (gridRows.length === 0) return []
        const snapshotLabel = formatShortDate(selectedSnapshot) || 'Snapshot'
        const configLabel = configEntries[selectedConfigIdx]?.label || 'Config'
        return [{
            name: `${snapshotLabel} - ${configLabel}`.slice(0, 31),
            data: gridRows,
            columns: shipType === 'B2B' ? b2bExcelCols : b2cExcelCols,
        }]
    }, [gridRows, selectedSnapshot, selectedConfigIdx, configEntries, shipType])

    const onSubmit = async (formData) => {
        if (!formData.refId) { enqueueSnackbar('Please select a merchant or courier', { variant: 'warning' }); return }
        // Capture search context for display
        const opts = formData.searchType === 'merchant' ? merchantOptions : courierOptions
        const match = opts.find(o => o.value === formData.refId)
        setSearchContext({ name: match?.label || formData.refId, type: formData.searchType, shipType: formData.shipType })
        setLoading(true); setSnapshots([]); setSearched(true); setSelectedSnapshotIdx(0); setSelectedConfigIdx(0)
        try {
            const res = await get(`config/rate-card/${formData.refId}/${formData.shipType}/history`)
            const body = res.data
            if (body?.status === 'SUCCESS' && Array.isArray(body.responseObj)) {
                setSnapshots(body.responseObj)
                if (body.responseObj.length === 0) enqueueSnackbar('No history found', { variant: 'info' })
            } else {
                enqueueSnackbar(body?.message || 'Failed to fetch history', { variant: 'error' })
            }
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || 'Error fetching history', { variant: 'error' })
        } finally { setLoading(false) }
    }

    const handleReset = () => { reset(); setSnapshots([]); setSearched(false); setSelectedSnapshotIdx(0); setSelectedConfigIdx(0); setSearchContext(null) }

    return (
        <>
            {loading && <Loader />}
            <PageTitle>Rate Card History</PageTitle>
            <Page title="">

                {/* ─── Search Card ─────────────────────────────────────────────── */}
                <Card sx={{ mb: 2.5, overflow: 'visible' }}>
                    <Box sx={{
                        background: COLORS.headerGradient,
                        px: 3, py: 2,
                        borderRadius: '16px 16px 0 0',
                        display: 'flex', alignItems: 'center', gap: 1.5,
                    }}>
                        <Box sx={{
                            width: 36, height: 36, borderRadius: 1.5,
                            backgroundColor: 'rgba(255,255,255,0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <HistoryIcon sx={{ color: '#fff', fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3 }}>Rate Card History</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem' }}>Search and browse historical rate card snapshots</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ px: 3, py: 2.5 }}>
                        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={2}>
                                    <RHFSelectField name="searchType" label="Search By" width="100%" options={SEARCH_TYPE_OPTIONS} size="small" />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <RHFSearchableSelectField
                                        name="refId"
                                        label={searchType === 'merchant' ? 'Select Merchant' : 'Select Courier'}
                                        options={searchType === 'merchant' ? merchantOptions : courierOptions}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <RHFSelectField name="shipType" label="Ship Type" width="100%" options={SHIP_TYPE_OPTIONS} size="small" />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Button
                                        type="submit" variant="contained" disabled={loading}
                                        startIcon={<SearchIcon />}
                                        sx={{ mr: 1.5, px: 3, py: '7.5px', borderRadius: 1.5, textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                                    >
                                        Search History
                                    </Button>
                                    <Button
                                        variant="outlined" onClick={handleReset}
                                        startIcon={<RestartAltIcon />}
                                        sx={{ px: 2.5, py: '7.5px', borderRadius: 1.5, textTransform: 'none', fontWeight: 600, borderColor: COLORS.grey300, color: COLORS.grey600, '&:hover': { borderColor: COLORS.grey600, backgroundColor: COLORS.grey50 } }}
                                    >
                                        Reset
                                    </Button>
                                </Grid>
                            </Grid>
                        </FormProvider>
                    </Box>
                </Card>

                {/* ─── Landing state ───────────────────────────────────────────── */}
                {!searched && snapshots.length === 0 && !loading && <LandingState />}

                {/* ─── Empty state ─────────────────────────────────────────────── */}
                {searched && !loading && snapshots.length === 0 && (
                    <Card sx={{ textAlign: 'center', py: 6, px: 3 }}>
                        <Box sx={{
                            width: 64, height: 64, borderRadius: 3, backgroundColor: COLORS.grey100,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
                        }}>
                            <HistoryIcon sx={{ fontSize: 32, color: COLORS.grey300 }} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: COLORS.grey800, mb: 0.5 }}>No History Found</Typography>
                        <Typography variant="body2" sx={{ color: COLORS.grey600, maxWidth: 400, mx: 'auto', lineHeight: 1.6 }}>
                            No rate card snapshots exist for the selected criteria. Try a different merchant/courier or ship type.
                        </Typography>
                    </Card>
                )}

                {/* ─── Results ─────────────────────────────────────────────────── */}
                {snapshots.length > 0 && (
                    <>
                        {/* ── Single horizontal controls bar ── */}
                        <Card sx={{ mb: 2 }}>
                            <Box sx={{
                                px: 2.5, py: 2,
                                display: 'flex', alignItems: 'center', gap: 2,
                                flexWrap: 'nowrap', overflowX: 'auto',
                                borderBottom: `1px solid ${COLORS.grey200}`,
                            }}>
                                {/* Snapshot selector */}
                                <TextField
                                    select size="small" label="Replaced On"
                                    value={selectedSnapshotIdx}
                                    onChange={(e) => { setSelectedSnapshotIdx(Number(e.target.value)); setSelectedConfigIdx(0) }}
                                    sx={{ minWidth: 280, flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem', fontWeight: 600 } }}
                                >
                                    {snapshots.map((s, i) => (
                                        <MenuItem key={i} value={i} sx={{ fontSize: '0.82rem' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                <AccessTimeIcon sx={{ fontSize: 13, color: COLORS.primary }} />
                                                <span>{formatSnapshotTime(s)}</span>
                                                {i === 0 && <Chip label="LATEST" size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, backgroundColor: COLORS.success, color: '#fff', ml: 'auto' }} />}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {/* Prev/Next nav */}
                                <SnapshotTimeline snapshots={snapshots} selectedIdx={selectedSnapshotIdx} onSelect={(i) => { setSelectedSnapshotIdx(i); setSelectedConfigIdx(0) }} />

                                {/* Config selector */}
                                {configEntries.length > 1 ? (
                                    <TextField
                                        select size="small" label="Rate Card Config"
                                        value={selectedConfigIdx}
                                        onChange={(e) => setSelectedConfigIdx(Number(e.target.value))}
                                        sx={{ minWidth: 260, flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem', fontWeight: 600 } }}
                                    >
                                        {configEntries.map((c, i) => (
                                            <MenuItem key={i} value={i} sx={{ fontSize: '0.82rem' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LocalShippingOutlinedIcon sx={{ fontSize: 14, color: COLORS.info }} />
                                                    <span>{c.label}</span>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                ) : configEntries.length === 1 && (
                                    <Box sx={{ flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: '0.65rem', color: COLORS.grey600, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
                                            Rate Card Config
                                        </Typography>
                                        <Chip
                                            icon={<LocalShippingOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                                            label={configEntries[0].label}
                                            sx={{ fontWeight: 600, fontSize: '0.78rem', backgroundColor: alpha(COLORS.primary, 0.06), color: COLORS.primary, border: `1px solid ${alpha(COLORS.primary, 0.15)}`, height: 32 }}
                                        />
                                    </Box>
                                )}

                                {/* Right side: action buttons */}
                                <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                                    <Typography sx={{ fontSize: '0.72rem', color: COLORS.grey600, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                        {gridRows.length} row{gridRows.length !== 1 ? 's' : ''} / {zoneCount} zone{zoneCount !== 1 ? 's' : ''}
                                    </Typography>
                                    {gridRows.length > 0 && (
                                        <DownloadExcelMultipleSheets
                                            sheets={excelSheets}
                                            filename={`RateCard_History_${shipType}_${formatShortDate(selectedSnapshot)}`}
                                        />
                                    )}
                                </Box>
                            </Box>

                            {/* Status strip: entity + metadata chips */}
                            <Box sx={{
                                px: 2.5, py: 1.25,
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                flexWrap: 'wrap',
                                backgroundColor: COLORS.grey50,
                            }}>
                                {searchContext && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Typography sx={{ fontSize: '0.72rem', color: COLORS.grey600, fontWeight: 500 }}>Showing history for</Typography>
                                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: COLORS.grey800 }}>{searchContext.name}</Typography>
                                        <Chip label={searchContext.shipType} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: alpha(COLORS.primary, 0.08), color: COLORS.primary }} />
                                    </Box>
                                )}
                                <Box sx={{ width: 1, height: 16, backgroundColor: COLORS.grey200, mx: 0.5 }} />
                                {selectedSnapshot?.rateCardData?.modifiedByUser && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PersonOutlineIcon sx={{ fontSize: 13, color: COLORS.grey600 }} />
                                        <Typography sx={{ fontSize: '0.72rem', color: COLORS.grey600 }}>
                                            by <span style={{ fontWeight: 600, color: COLORS.grey800 }}>{selectedSnapshot.rateCardData.modifiedByUser}</span>
                                        </Typography>
                                    </Box>
                                )}
                                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {selectedConfig?.active && (
                                        <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: '13px !important' }} />} label="Active" size="small"
                                            sx={{ fontWeight: 600, fontSize: '0.68rem', backgroundColor: COLORS.successLight, color: COLORS.success, border: `1px solid ${alpha(COLORS.success, 0.2)}` }} />
                                    )}
                                    {selectedConfig?.startDate && (
                                        <Chip icon={<CalendarTodayOutlinedIcon sx={{ fontSize: '12px !important' }} />} label={`Start: ${selectedConfig.startDate}`} size="small"
                                            sx={{ fontWeight: 600, fontSize: '0.68rem', backgroundColor: '#fff', color: COLORS.grey600, border: `1px solid ${COLORS.grey200}` }} />
                                    )}
                                    {selectedConfig?.cplist?.length > 0 && (
                                        <Tooltip title={selectedConfig.cplist.join(', ')} arrow>
                                            <Chip icon={<LocalShippingOutlinedIcon sx={{ fontSize: '12px !important' }} />}
                                                label={selectedConfig.cplist.length === 1 ? selectedConfig.cplist[0] : `${selectedConfig.cplist.length} couriers`}
                                                size="small" sx={{ fontWeight: 600, fontSize: '0.68rem', backgroundColor: alpha(COLORS.info, 0.08), color: COLORS.info, border: `1px solid ${alpha(COLORS.info, 0.2)}` }} />
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        </Card>

                        {/* Full-width data panel */}
                        <Box sx={{ width: '100%' }}>
                                {/* Data grid */}
                                <Card>
                                    <Box sx={{ position: 'relative' }}>
                                        <Box sx={{
                                            position: 'absolute', top: 0, right: 0, bottom: 0, width: 32,
                                            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.9))',
                                            pointerEvents: 'none', zIndex: 1,
                                        }} />
                                        <Box sx={{ px: 0.5 }}>
                                            {gridLoading ? (
                                                <GridSkeleton />
                                            ) : gridRows.length > 0 ? (
                                                <ProzoDataGrid
                                                    columns={currentColumns}
                                                    rows={gridRows}
                                                    getRowId={(row) => row._id}
                                                    autoHeight
                                                    hideFooterPagination={gridRows.length <= 100}
                                                    paginationMode="client"
                                                    sizePerPage={50}
                                                />
                                            ) : (
                                                <Box sx={{ textAlign: 'center', py: 5 }}>
                                                    <TableRowsOutlinedIcon sx={{ fontSize: 40, color: COLORS.grey300, mb: 1 }} />
                                                    <Typography variant="body2" sx={{ color: COLORS.grey600 }}>No rate card slab data in this configuration.</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Card>
                        </Box>
                    </>
                )}
            </Page>
        </>
    )
}

export default RateCardHistory
